import { GroupSecurity } from '@quromedical/fhir-common';
import type {
  PatientCoverageResponse,
  PatientResponse,
  PersonContact,
  PersonGeneral,
} from '@quromedical/models';
import { Section, Skeleton, SkeletonProvider } from 'design-system';
import { onMapsPress } from 'helpers/platform-links';
import { useUserSession } from 'hooks';
import { useRevalidation } from 'hooks/useRevalidation';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
} from 'integration';
import { PatientApi } from 'integration/aggregate';
import type React from 'react';
import { useCallback } from 'react';
import { CareTeamCard } from 'screens/patient';
import { PersonContactForm, PersonGeneralForm } from 'screens/person';

interface PatientProfileTabProps {
  patientId: string;
  patient?: PatientResponse;
  coverage?: PatientCoverageResponse;
  isValidating: boolean;
}

const patientApi = new PatientApi();

export const PatientProfileTab: React.FC<PatientProfileTabProps> = ({
  patientId,
  patient,
  coverage,
  isValidating,
}) => {
  const session = useUserSession();
  const revalidate = useRevalidation(patientId);

  const canEdit = session.hasAny([GroupSecurity.PatientUpdate]);

  // if we have a medical aid then the general details are non-editable
  const canEditGeneral = canEdit && !coverage;

  const onGeneralSubmit = useCallback(
    async (formData: PersonGeneral) => {
      await patientApi.updateGeneralPatient({
        id: patientId,
        general: formData,
      });
    },
    [patientId],
  );

  const onContactSubmit = useCallback(
    async (results: PersonContact) => {
      await patientApi.updateContactPatient(patientId, results);
    },
    [patientId],
  );

  const isButtonDisabled = !patient?.contact?.addressCoordinates;

  const onMapsPressed = useCallback(() => {
    onMapsPress(patient?.contact);
  }, [patient?.contact]);

  return (
    <SkeletonProvider loading={isValidating}>
      <Section>
        <Skeleton>
          <PersonGeneralForm
            canEdit={canEditGeneral}
            revalidate={revalidate}
            data={patient?.general}
            onSubmit={onGeneralSubmit}
          />
        </Skeleton>
      </Section>

      <Section>
        <Skeleton>
          <PersonContactForm
            canEdit={canEdit}
            data={patient?.contact}
            onSubmit={onContactSubmit}
            revalidate={revalidate}
            hasEmergencyAccess
            addressFetcher={googleSearchAddressFetcher}
            getAddressPartial={getPlaceAddressPartialFetcher}
            onMapsPress={onMapsPressed}
            isButtonDisabled={isButtonDisabled}
          />
        </Skeleton>
      </Section>

      <Section hasBottomMargin>
        <Skeleton>
          <CareTeamCard
            revalidate={revalidate}
            canEdit={canEdit}
            patientId={patientId}
            generalPractitioner={patient?.generalPractitioner}
          />
        </Skeleton>
      </Section>
    </SkeletonProvider>
  );
};
